import { DeleteOutlined } from '@ant-design/icons';
import { useUser, withPageAuthRequired } from '@auth0/nextjs-auth0';
import {
  AutoComplete,
  Button,
  Col,
  message,
  Popconfirm,
  Row,
  Skeleton,
  Space,
} from 'antd';
import { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { generateEventData, identify } from '@/analytics';
import {
  DraftOrderConfirmed,
  draftOrderConfirmed,
  DraftOrdersViewed,
  draftOrdersViewed,
} from '@/analytics/segment';
import formatCurrency from '@/components/common/Extensions/CurrencyExtensions';
import DraftOrdersListPage from '@/components/purchase-orders/draft-orders';
import TabMenu from '@/components/purchase-orders/tab-menu';
import {
  useCreateLineItemMutation,
  useGetReupProductsQuery,
  useGetReupPurchaseOrdersQuery,
  useGetReupSuppliersQuery,
  useUpdateLineItemMutation,
  useUpdatePurchaseOrderMutation,
} from '@/graphql/hooks';
import {
  CreateReupLineItemInput,
  License,
  PurchaseOrderStatus,
  Reup_Product,
  ReupPurchaseOrder,
  ReupPurchaseOrderFilter,
  UpdateReupLineItemInput,
  UpdateReupPurchaseOrderInput,
} from '@/graphql/types';
import { useGetBuyerLicenseByLicenseNumber } from '@/lib/hooks';
import { LineItem, PurchaseOrder } from '@/types';

export const pOsFromReupPos = (
  orgkey: string,
  license: License,
  reupPos: ReupPurchaseOrder[],
  products: Reup_Product[],
  statusList?: PurchaseOrderStatus[]
): PurchaseOrder[] => {
  let filteredPos: PurchaseOrderStatus[] = [PurchaseOrderStatus.Draft];

  if (statusList && statusList.length > 0) {
    filteredPos = statusList;
  }

  const pos = reupPos
    .filter((po) => filteredPos.includes(po.status as PurchaseOrderStatus))
    .map((p) => {
      let supplierLicense = '';

      const lineItems = p?.reupLineItems.nodes
        .filter((li) => !li?.isDeleted)
        .map((li) => {
          const product = products.find((lp) => lp.key === li?.productId);

          if (supplierLicense && supplierLicense.length <= 0) {
            supplierLicense =
              product && product?.Suppliers && product?.Suppliers[0]
                ? (product?.Suppliers[0]?.SupplierLicenseNumber as string)
                : '';
          }
          const lineItem: LineItem = {
            Id: li?.id.toString() ?? '',
            PurchaseOrderId: li?.purchaseOrderId
              ? li?.purchaseOrderId.toString()
              : '',
            UnitPrice: li?.unitPrice as number,
            RequestedQuantity: li?.requestedQuantity ?? 0,
            TotalPrice: Number(li?.totalPrice),
            OrderSessionId: 'TestSession',
            IsDeleted: li?.isDeleted ?? false,
            Product: {
              key: li?.productId ?? '',
              ProductName: li?.productName ?? '',
              CategoryType: product?.CategoryType ?? '',
              OrganizationKey: orgkey,
              StoreId: product?.StoreId ?? '',
              StoreKey: product?.StoreKey ?? '',
              StoreName: product?.StoreName ?? '',
              QuantityOnHand: product?.QuantityOnHand ?? 0,
            },
          };

          return lineItem;
        });

      const po: PurchaseOrder = {
        Id: p?.id ? p?.id.toString() : '', // ToDo: This need to always be set and not null. Need to make sure the Id type is not null in graphql
        SupplierId: p?.supplierId ?? 'NA',
        SupplierName: p?.supplierName ?? 'NA',
        SupplierLicenseNumber: supplierLicense,
        PoNumber: p?.poNumber ?? 'NA',
        Note: p?.note ?? '',
        PurchaseOrderStatus: p.status as PurchaseOrderStatus,
        LineItems: lineItems ?? [],
        DateCreated: new Date(),
        DateUpdated: new Date(),
        TotalPrice: p.totalPrice as number,
        OrganizationKey: orgkey,
        BuyerId: license.id,
      };

      return po;
    });

  return pos;
};
const DraftOrdersPage: NextPage = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([] as PurchaseOrder[]);
  const [selectedOrders, setSelectedOrders] = useState([] as PurchaseOrder[]);
  const [licenseNumber, setLicenseNumber] = useState('');
  const [storeLicense, setStoreLicense] = useState({} as License);
  const [trackingComplete, setTrackingComplete] = useState(false);
  const [isLoaded, setLoaded] = useState(false);

  const {
    query: { org, store, pathName },
    pathname,
  } = useRouter();

  const orgkey = org as string;
  const storekey = store as string;

  const { user } = useUser();

  const { data, isFetched } = useGetReupProductsQuery({
    orgkey,
    storekey,
  });

  const poFilter: ReupPurchaseOrderFilter = {
    buyerId: {
      equalTo: storeLicense.id,
    },
    isDeleted: { equalTo: false },
    organizationKey: {
      equalTo: orgkey,
    },
  };

  const {
    data: poData,
    isFetched: poFetched,
    refetch: reloadPos,
  } = useGetReupPurchaseOrdersQuery(
    {
      poFilter,
    },
    { enabled: storeLicense.id > 0 }
  );

  const { data: supplierData, isFetched: suppliersFetched } =
    useGetReupSuppliersQuery({ orgkey });

  const { license, licensesFetched } =
    useGetBuyerLicenseByLicenseNumber(licenseNumber);

  const { mutate: updatePo } = useUpdatePurchaseOrderMutation();

  const { mutate: updateLineItem } = useUpdateLineItemMutation();

  const { mutate: createLineItem } = useCreateLineItemMutation();

  useEffect(() => {
    if (license && licensesFetched) {
      setStoreLicense(license);
    }

    if (isFetched && data?.products) {
      setLicenseNumber(data?.products[0].LicenseNum as string);
    }

    if (suppliersFetched && isFetched && poFetched) {
      if (data && data?.products && poData && poData.purchaseOrders?.nodes) {
        const pos = pOsFromReupPos(
          orgkey,
          storeLicense,
          poData.purchaseOrders?.nodes as ReupPurchaseOrder[],
          data?.products
        );
        setPurchaseOrders(pos);
      }

      setLoaded(true);
    }
  }, [
    supplierData,
    suppliersFetched,
    data,
    isFetched,
    setLoaded,
    isLoaded,
    orgkey,
    poFetched,
    poData,
    licensesFetched,
    license,
    storeLicense,
  ]);

  // Segment Draft Orders Viewed
  const draftOrderViewedEvent = useCallback(() => {
    draftOrdersViewed(
      generateEventData(
        orgkey,
        storekey,
        storeLicense.licenseNumber,
        storeLicense.state,
        {
          email: user?.email,
          draftOrders: purchaseOrders,
        }
      ) as DraftOrdersViewed
    );

    identify({
      CurrentAccountLink: pathName as string,
      license: {
        licenseNumber: storeLicense.licenseNumber,
        licenseName: storeLicense.licenseName,
      },
      Email: user?.email as string,
      Name: user?.name as string,
      Product: 'Reup',
    });
  }, [
    orgkey,
    storekey,
    storeLicense.licenseNumber,
    storeLicense.state,
    storeLicense.licenseName,
    user?.email,
    user?.name,
    purchaseOrders,
    pathName,
  ]);

  useEffect(() => {
    if (
      licensesFetched &&
      poFetched &&
      storeLicense?.licenseNumber &&
      !trackingComplete
    ) {
      draftOrderViewedEvent();
      setTrackingComplete(true);
    }
  }, [
    draftOrderViewedEvent,
    licensesFetched,
    poFetched,
    storeLicense?.licenseNumber,
    trackingComplete,
  ]);

  const getSelectedOrdersTotal = () => {
    const sumSelectedOrders = selectedOrders
      .map((item) => item.LineItems.reduce((p, c) => p + c.TotalPrice, 0))
      .reduce((prev, curr) => prev + curr, 0);

    return formatCurrency(sumSelectedOrders);
  };

  const onSelectedOrdersChange = (orders: PurchaseOrder[]) =>
    setSelectedOrders(orders);

  const onAutoCompleteSelect = (productId: string, purchaseOrderId: string) => {
    const product = data?.products?.find((p) => p.key === productId);

    const purchaseOrder = purchaseOrders.find(
      (po) => po.Id === purchaseOrderId
    );

    if (product && purchaseOrder) {
      const unitPrice = product.Suppliers
        ? product.Suppliers[0].UnitCost || 0
        : 0;

      // Add 1 to the cart
      const lineTotalPrice = unitPrice * 1;

      const input: CreateReupLineItemInput = {
        reupLineItem: {
          productId: product.key,
          purchaseOrderId: Number(purchaseOrder.Id),
          productName: product.ProductName,
          requestedQuantity: 1,
          unitPrice: product.Suppliers ? product.Suppliers[0].UnitCost || 0 : 0,
          totalPrice: lineTotalPrice,
        },
      }; // Recalculate Total

      const poUpdateInput: UpdateReupPurchaseOrderInput = {
        id: Number(purchaseOrder.Id),
        patch: {
          totalPrice: purchaseOrder.TotalPrice + lineTotalPrice,
        },
      };

      void createLineItem(
        {
          input,
        },
        {
          onSuccess: () => {
            void updatePo(
              { input: poUpdateInput },
              {
                onSuccess: () => {
                  void message.success('Product added to order');
                  reloadDraftOrders();
                },
                onError: () => {
                  void message.error(
                    'Unable to add the product. Please refresh the page and try again. Reach out to chat support if problems continue.'
                  );
                },
                onSettled: () => {
                  setLoaded(true);
                },
              }
            );
            void message.success('Product added to order');
            reloadDraftOrders();
          },
          onError: () => {
            void message.error(
              'Unable to add the product. Please refresh the page and try again. Reach out to chat support if problems continue.'
            );
          },
        }
      );
    } else {
      void message.error('The product or order could not be found');
    }
  };

  const onNoteUpdate = (purchaseOrderId: string, note: string) => {
    const input: UpdateReupPurchaseOrderInput = {
      id: Number(purchaseOrderId),
      patch: {
        note,
      },
    };

    void updatePo(
      { input },
      {
        onSuccess: () => {
          void message.success('Note updated');
          reloadDraftOrders();
        },
        onError: () => {
          void message.error(
            'Unable to update the note. Please refresh the page and try again. Reach out to chat support if problems continue.'
          );
        },
        onSettled: () => {
          setLoaded(true);
        },
      }
    );
  };

  const onDeleteOrders = (orderIds: string[]) => {
    const deletedOrders = purchaseOrders.filter((po) =>
      orderIds.includes(po.Id)
    );

    setLoaded(false);
    for (const v of deletedOrders) {
      const input: UpdateReupPurchaseOrderInput = {
        id: Number(v.Id),
        patch: {
          status: PurchaseOrderStatus.Deleted,
          isDeleted: true,
        },
      };

      void updatePo(
        { input },
        {
          onSuccess: () => {
            const messageText =
              deletedOrders.length > 1
                ? `${deletedOrders.length} orders deleted`
                : `${deletedOrders[0].SupplierName} order deleted.`;
            void message.success(messageText);
            setSelectedOrders([]);
            reloadDraftOrders();
          },
          onError: () => {
            void message.error(
              'Unable to delete orders. Please refresh the page and try again. Reach out to chat support if problems continue.'
            );
          },
          onSettled: () => {
            setLoaded(true);
          },
        }
      );
    }
  };

  const onLineItemRemoved = (
    lineItem: LineItem,
    purchaseOrder: PurchaseOrder
  ) => {
    purchaseOrder.LineItems = purchaseOrder?.LineItems.filter(
      (p) => p.Id !== lineItem.Id
    );

    // Recalculate Total
    const poTotal = purchaseOrder.LineItems.map((li) => li.TotalPrice).reduce(
      (a, b) => a + b,
      0
    );
    const poUpdateInput: UpdateReupPurchaseOrderInput = {
      id: Number(purchaseOrder.Id),
      patch: {
        totalPrice: poTotal,
      },
    };
    const lineItemUpdateInput: UpdateReupLineItemInput = {
      id: Number(lineItem.Id),
      patch: {
        isDeleted: true,
      },
    };
    const errorMessage = () => {
      void message.error(
        `Unable to remove the line item ${lineItem.Product?.ProductName ?? ''}`
      );
    };
    // Update the new PO total
    // ToDo: Think about putting this style logic into a DAL.
    updatePo(
      {
        input: poUpdateInput,
      },
      {
        onSuccess: () => {
          // Update the Line Item
          updateLineItem(
            {
              input: lineItemUpdateInput,
            },
            {
              onSuccess: () => {
                void message.success('Product removed from order');
                reloadDraftOrders();
              },
              onError: errorMessage,
            }
          );
        },
        onError: errorMessage,
      }
    );

    setPurchaseOrders(
      purchaseOrders.map((po) =>
        po.Id === purchaseOrder.Id ? purchaseOrder : po
      )
    );
  };

  const onQuantityChanged = (lineItem: LineItem) => {
    const order = purchaseOrders.find((p) => p.Id === lineItem.PurchaseOrderId);
    if (order) {
      const lineItemIndex = order.LineItems.findIndex(
        (li) => li.Id === lineItem.Id
      );

      // Recalculate the line item total
      lineItem.TotalPrice =
        lineItem.RequestedQuantity && lineItem.UnitPrice
          ? lineItem.RequestedQuantity * lineItem.UnitPrice
          : 0;

      order.LineItems[lineItemIndex] = lineItem;

      // Recalculate Total
      const poTotal = order.LineItems.map((li) => li.TotalPrice).reduce(
        (a, b) => a + b,
        0
      );

      const poUpdateInput: UpdateReupPurchaseOrderInput = {
        id: Number(order.Id),
        patch: {
          totalPrice: poTotal,
        },
      };

      const lineItemUpdateInput: UpdateReupLineItemInput = {
        id: Number(lineItem.Id),
        patch: {
          totalPrice: lineItem.TotalPrice,
          requestedQuantity: lineItem.RequestedQuantity,
        },
      };

      const errorMessage = () => {
        void message.error(
          `Unable to adjust the quantity of the item ${
            lineItem.Product?.ProductName ?? ''
          }`
        );
      };

      // Update the new PO total
      // ToDo: Think about putting this style logic into a DAL.
      updatePo(
        {
          input: poUpdateInput,
        },
        {
          onSuccess: () => {
            // Update the Line Item
            updateLineItem(
              {
                input: lineItemUpdateInput,
              },
              {
                onSuccess: () => {
                  void message.success('Order quantity updated');
                  reloadDraftOrders();
                },
                onError: errorMessage,
              }
            );
          },
          onError: errorMessage,
        }
      );

      setPurchaseOrders(
        purchaseOrders.map((po) => (po.Id === order.Id ? order : po))
      );
    }
  };

  const reloadDraftOrders = () => {
    void (async () => {
      const { data: draftPos } = await reloadPos();
      if (
        data &&
        data?.products &&
        draftPos &&
        draftPos.purchaseOrders?.nodes
      ) {
        const pos = pOsFromReupPos(
          orgkey,
          storeLicense,
          draftPos?.purchaseOrders?.nodes as ReupPurchaseOrder[],
          data?.products
        );

        setPurchaseOrders(pos);
      }
    })();
  };

  const onConfirmDraftOrders = () => {
    if (selectedOrders.length > 0) {
      setLoaded(false);
      for (const v of selectedOrders) {
        const input: UpdateReupPurchaseOrderInput = {
          id: Number(v.Id),
          patch: {
            status: PurchaseOrderStatus.Placed,
          },
        };

        void updatePo(
          { input },
          {
            onSuccess: () => {
              void message.success(
                `${selectedOrders.length} order${
                  selectedOrders.length > 1 ? 's' : ''
                } saved.`
              );

              // Segment Draft Order Confirmed
              for (const po of selectedOrders) {
                draftOrderConfirmed(
                  generateEventData(
                    orgkey,
                    storekey,
                    storeLicense.licenseNumber,
                    storeLicense.state,
                    {
                      email: user?.email,
                      draftOrder: po,
                    }
                  ) as DraftOrderConfirmed
                );
              }

              setSelectedOrders([]);
              reloadDraftOrders();
            },
            onError: () => {
              void message.error(
                'Unable to save changes. Please refresh the page and try again. Reach out to chat support if problems continue.'
              );
            },
            onSettled: () => {
              setLoaded(true);
            },
          }
        );
      }
    }
  };

  return (
    <div>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      </Head>
      <div className="p-6 mb-6 bg-white border border-solid border-gray-300">
        <Row>
          <Col flex="auto">
            <TabMenu
              draftOrderCount={purchaseOrders.length}
              pathName={pathname}
              orgKey={orgkey}
              storeKey={storekey}
            />
          </Col>
          <Col span={12} className="min-w-6/12">
            <div className="flex flex-col justify-between h-full items-end">
              <AutoComplete
                style={{ width: '100%', maxWidth: '360px' }}
                options={purchaseOrders.map((p) => ({
                  value: p.Id,
                  label: p.PoNumber,
                }))}
                placeholder="Search orders"
                onSelect={(value: string) => console.log(value)}
              />

              <div className="flex flex-col items-end">
                <Space align="baseline">
                  <h5>
                    {selectedOrders.length} order
                    {selectedOrders.length > 1 ? 's' : ''} selected | total{' '}
                    {getSelectedOrdersTotal()}
                  </h5>
                  <Button
                    disabled={selectedOrders.length <= 0}
                    type="primary"
                    onClick={onConfirmDraftOrders}
                    loading={!isLoaded}
                  >
                    Confirm Orders
                  </Button>
                  <Popconfirm
                    title={
                      <div>
                        <div>Delete Orders?</div>
                        <div />
                        <p>We won’t be able to bring them back</p>
                      </div>
                    }
                    okText="Yes, delete"
                    okButtonProps={{ type: 'primary' }}
                    cancelText="Cancel"
                    onConfirm={() =>
                      onDeleteOrders(selectedOrders.map((so) => so.Id))
                    }
                  >
                    <Button
                      disabled={selectedOrders.length <= 0}
                      icon={<DeleteOutlined />}
                    >
                      Delete Orders
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="p-6 bg-white mb-6 border border-solid border-gray-300">
        {isLoaded && (
          <DraftOrdersListPage
            purchaseOrders={purchaseOrders}
            onLineItemRemoved={onLineItemRemoved}
            onLineItemQuantityUpdated={onQuantityChanged}
            onNoteUpdate={onNoteUpdate}
            onDeleteOrders={onDeleteOrders}
            selectedOrders={selectedOrders}
            onSelectedOrdersChange={onSelectedOrdersChange}
            onAutoCompleteSelect={onAutoCompleteSelect}
            products={data?.products as Reup_Product[]}
          />
        )}
        {!isLoaded && <Skeleton active />}
      </div>
    </div>
  );
};

export default withPageAuthRequired(DraftOrdersPage);
